
import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { CircuitBoard, Building2, BookOpen, Home, GraduationCap, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const KarenProfile = () => {
  const user = {
    name: "Karen Watson",
    role: "iSPAI Chief Consumer",
    email: "karen.watson@ispai.io",
    solutionBounties: 8,
    domains: [
      "Knowledge Management",
      "AI Integration",
      "Web3 Technologies",
      "Consumer Insights",
      "Digital Transformation",
      "Enterprise Solutions",
      "Innovation Strategy",
      "Strategic Sourcing",
      "Change Management"
    ],
    industries: [
      { name: "Technology", icon: CircuitBoard },
      { name: "Enterprise", icon: Building2 },
      { name: "Education", icon: GraduationCap },
      { name: "Research", icon: BookOpen },
      { name: "Sustainable Housing", icon: Home }
    ],
    confirmedSolutions: 28,
    confirmedKnowledgeBases: 45,
    tokenBalance: 175,
    bio: "Karen Watson is the Chief Consumer at iSPAI, with over 20 years of experience in management consulting, specializing in business consulting, strategic sourcing, change management, and IT consulting. She will work closely with iSPAI's Chief Creator to ensure a frictionless iSPAI flywheel that engages and enables Consumers and Creators alike. Known for driving operational efficiency and transformational change, she has delivered results through the use of advanced technologies. Karen Watson has driven business strategy and cost-reduction efforts saving $100 million for Fortune 500 clients while promoting diversity in tech. She has led strategic sourcing initiatives influencing fuel standards legislation and delivering major cost savings. Karen is a leader in digital transformation, strategic planning, and inclusive leadership, aligning with iSPAI's mission to democratize knowledge and empower the workforce. As the Chief Consumer Karen will focus on the strategic sourcing of knowledge and wisdom for GenAI by enterprise consumers."
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/3a148e9a-7de2-4aee-bda7-df2eda17091e.png"
                    imageAlt="Karen Watson"
                  >
                    <Avatar className="h-48 w-48 cursor-pointer">
                      <AvatarImage 
                        src="/lovable-uploads/3a148e9a-7de2-4aee-bda7-df2eda17091e.png" 
                        alt="Karen Watson"
                      />
                      <AvatarFallback>KW</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-16">BIO</Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96">
                        <p className="text-sm leading-6">{user.bio}</p>
                      </PopoverContent>
                    </Popover>
                    <Button variant="outline" className="w-16">NW</Button>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div 
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ImageHoverPopup 
                      imageSrc="/lovable-uploads/3a148e9a-7de2-4aee-bda7-df2eda17091e.png"
                      imageAlt="Karen Watson"
                    >
                      <h2 className="font-serif text-3xl font-bold text-black cursor-pointer hover:text-primary transition-colors">{user.name}</h2>
                    </ImageHoverPopup>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                    <a href="/ispai-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d7afdb11-1c93-4596-9b11-91cb70981610.png" 
                        alt="iSPAI Channel" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </a>
                  </div>
                  <p className="text-lg font-medium text-black mb-4">{user.role}</p>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link 
                          to="/solution-bounties/karen"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={9.5} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge 
                    key={domain} 
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default KarenProfile;
